/**
 * SOURCE_TIERS — single source of truth for AI source labelling.
 *
 * Every reference Kuutio shows in AI output (SWOT, chat, report citations, lens
 * summaries) carries a `tier` field. The UI groups by tier and renders the
 * matching icon + label from this registry. Labels and disclaimers are in the
 * locale files (`sourceTiers` namespace): translate `labelKey` / `disclaimerKey`.
 *
 * Tiers:
 *   rag     — research: the instance's research datasets (live)
 *   policy  — policy-tagged sources: framing, never evidence (specs/knowledge-kinds.md)
 *   opinion — opinion-tagged sources: what people think, never fact
 *   testimony — first-hand reporting by the people involved (testimony pipeline)
 *   api     — vetted open-data APIs (Tilastokeskus, Suomi.fi, OECD)
 *   human   — the workshop's own material
 *   web     — Anthropic web_search server tool, unvetted
 *
 * Add a tier here once and every consumer of <SourceList> picks it up.
 */
import { BookOpen, Globe, Landmark, Users, Scale, MessageCircle, NotebookPen } from 'lucide-react';

export const SOURCE_TIERS = {
  rag: {
    id: 'rag',
    icon: BookOpen,
    labelKey: 'sourceTiers.rag',
    color: '#1e7d3a',
    bg: 'rgba(30, 125, 58, 0.12)',
    border: 'rgba(30, 125, 58, 0.45)',
    disclaimerKey: null,
  },
  policy: {
    id: 'policy',
    icon: Scale,
    labelKey: 'sourceTiers.policy',
    color: '#a16207',
    bg: 'rgba(161, 98, 7, 0.12)',
    border: 'rgba(161, 98, 7, 0.45)',
    disclaimerKey: 'sourceTiers.policyDisclaimer',
  },
  opinion: {
    id: 'opinion',
    icon: MessageCircle,
    labelKey: 'sourceTiers.opinion',
    color: '#be185d',
    bg: 'rgba(190, 24, 93, 0.12)',
    border: 'rgba(190, 24, 93, 0.45)',
    disclaimerKey: 'sourceTiers.opinionDisclaimer',
  },
  testimony: {
    id: 'testimony',
    icon: NotebookPen,
    labelKey: 'sourceTiers.testimony',
    color: '#0e7490',
    bg: 'rgba(14, 116, 144, 0.12)',
    border: 'rgba(14, 116, 144, 0.45)',
    disclaimerKey: 'sourceTiers.testimonyDisclaimer',
  },
  web: {
    id: 'web',
    icon: Globe,
    labelKey: 'sourceTiers.web',
    color: '#c2410c',
    bg: 'rgba(194, 65, 12, 0.12)',
    border: 'rgba(194, 65, 12, 0.45)',
    disclaimerKey: 'sourceTiers.webDisclaimer',
  },
  api: {
    id: 'api',
    icon: Landmark,
    labelKey: 'sourceTiers.api',
    color: '#1e3a8a',
    bg: 'rgba(30, 58, 138, 0.12)',
    border: 'rgba(30, 58, 138, 0.45)',
    disclaimerKey: null,
  },
  human: {
    id: 'human',
    icon: Users,
    labelKey: 'sourceTiers.human',
    color: '#7c3aed',
    bg: 'rgba(124, 58, 237, 0.12)',
    border: 'rgba(124, 58, 237, 0.45)',
    disclaimerKey: null,
  },
};

// Display order — research first, then what frames or colours it, web last with its disclaimer.
export const TIER_ORDER = ['rag', 'api', 'policy', 'opinion', 'testimony', 'human', 'web'];

export function getTier(tierId) {
  return SOURCE_TIERS[tierId] || SOURCE_TIERS.rag;
}

/** The tier a retrieved chunk belongs to, from its kind (its source's tag; specs/knowledge-kinds.md). */
export function tierOfKind(knowledgeKind) {
  if (['policy', 'opinion', 'testimony'].includes(knowledgeKind)) return knowledgeKind;
  if (knowledgeKind === 'kuutio') return 'human';
  return 'rag';
}
